import { useAuth } from "@/src/contexts/auth.context";
import { EventItem, RSVPStatus } from "@/src/models/event.model";
import { isEventFull, partyFits } from "@/src/utils/event-capacity.utils";
import {
  useDeleteRSVPMutation,
  useGetAttendeeCountQuery,
  useGetEventSeatCountQuery,
  useGetUserRSVPQuery,
  useInsertRSVPMutation,
  useLeaveEventMutation,
  useUpdateRSVPStatusMutation,
} from "@/src/services/api/events/events.hooks";
import Toast from "react-native-toast-message";

export const useEventRSVP = (event: EventItem) => {
  const { user } = useAuth();
  const currentUserId = user?.itemId ?? "";

  const isRegistrationRequired = event.registrationPolicy === "Required";

  const { data: userRsvp } = useGetUserRSVPQuery(currentUserId, event.ItemId, {
    enabled: isRegistrationRequired,
  });
  const { data: attendeeCount = 0 } = useGetAttendeeCountQuery(event.ItemId, {
    enabled: isRegistrationRequired,
  });
  // Seats = attendees + their guests. Capacity is measured in seats.
  const { data: seatCount } = useGetEventSeatCountQuery(event.ItemId, {
    enabled: isRegistrationRequired,
  });
  const seatsTaken = seatCount?.seats ?? attendeeCount;

  const isAttending =
    isRegistrationRequired && userRsvp?.status === RSVPStatus.GOING;
  const isWaitlisted =
    isRegistrationRequired && userRsvp?.status === RSVPStatus.WAITING;
  // CR-B2-08: Declined is distinct from no-RSVP-row and joined-then-left (deleted row)
  const isDeclined =
    isRegistrationRequired && userRsvp?.status === RSVPStatus.DECLINED;

  const seatState = {
    seatsTaken,
    capacity: event.capacity,
    isRegistrationRequired,
  };
  const isFull = isEventFull(seatState);

  const { mutate: insertRSVP, isPending: isJoining } = useInsertRSVPMutation();
  const { mutate: updateRSVPStatus, isPending: isUpdatingStatus } =
    useUpdateRSVPStatusMutation();
  const { mutateAsync: deleteRSVPAsync, isPending: isDeletingForDecline } =
    useDeleteRSVPMutation();
  const { mutate: insertRSVPAsync } = useInsertRSVPMutation();
  const { mutate: leaveEvent, isPending: isLeaving } = useLeaveEventMutation(
    event.ItemId,
    event.capacity,
  );

  const participantSnapshot = {
    firstName: user?.firstName ?? "",
    lastName: user?.lastName ?? "",
    email: user?.email ?? "",
    profileImageUrl: user?.profileImageUrl ?? "",
  };

  // CR-B2-09: capacity enforcement — full events route to WAITING with user notification
  /**
   * Guests consume capacity, so joining with N guests needs N+1 free seats —
   * checking `isFull` alone would let a party overflow the event by bringing
   * guests into the last remaining seat.
   */
  const handleJoin = (guestNames: string[] = []) => {
    const newStatus = partyFits(seatState, guestNames.length)
      ? RSVPStatus.GOING
      : RSVPStatus.WAITING;

    if (isDeclined && userRsvp) {
      // CR-B2-08: declined → going/waiting: update the existing Declined row in-place
      updateRSVPStatus({ itemId: userRsvp.ItemId, status: newStatus });
      return;
    }

    insertRSVP(
      {
        eventId: event.ItemId,
        status: newStatus,
        eventParticipantSnapshot: participantSnapshot,
        guestNames,
      },
      {
        onSuccess: () => {
          if (newStatus === RSVPStatus.WAITING) {
            Toast.show({ type: "info", text1: "LABEL_ADDED_TO_WAITLIST" });
          }
        },
      },
    );
  };

  const handleLeave = (rsvpItemId: string) => {
    leaveEvent({ itemId: rsvpItemId });
  };

  // CR-B2-08: Decline — frees a seat if Going (via leaveEvent which promotes waitlist)
  const handleDecline = async () => {
    if (isAttending && userRsvp) {
      // Going → Declined: delete first (frees seat + promotes waitlist), then insert Declined
      try {
        await deleteRSVPAsync({ itemId: userRsvp.ItemId });
        insertRSVPAsync({
          eventId: event.ItemId,
          status: RSVPStatus.DECLINED,
          eventParticipantSnapshot: participantSnapshot,
        });
      } catch {
        Toast.show({ type: "error", text1: "ERROR_RSVP_DECLINE_FAILED" });
      }
    } else if (isWaitlisted && userRsvp) {
      // Waiting → Declined: update in-place (waitlist spot freed, no seat to release)
      updateRSVPStatus({ itemId: userRsvp.ItemId, status: RSVPStatus.DECLINED });
    } else if (!userRsvp) {
      // No existing RSVP → insert Declined
      insertRSVP({
        eventId: event.ItemId,
        status: RSVPStatus.DECLINED,
        eventParticipantSnapshot: participantSnapshot,
      });
    }
  };

  const isDeclining = isDeletingForDecline || isUpdatingStatus;

  const joinButtonText = isAttending
    ? "BTN_LEAVE"
    : isWaitlisted
      ? "BTN_LEAVE_WAITLIST"
      : isFull
        ? "BTN_JOIN_WAITLIST"
        : "BTN_JOIN";

  return {
    userRsvp,
    attendeeCount,
    seatsTaken,
    guestCount: seatCount?.guests ?? 0,
    isAttending,
    isWaitlisted,
    isDeclined,
    isFull,
    isJoining,
    isLeaving,
    isDeclining,
    joinButtonText,
    handleJoin,
    handleLeave,
    handleDecline,
  };
};
